'use client';

import { c as _c } from "react/compiler-runtime";
import { CART_DURATION_HALF_DAY, type CartDuration } from '@funkey/constants';
import { DateTimeSDK, maybeMap } from '@funkey/shared';
import { graphql } from '@funkey/types/graphql/gql';
import { CACHED_PARTICIPANTS_FILTER } from '~/features/activities/search/client/context';
import { useLastLocationQuery, useLastLocationRadius } from '~/features/activities/useLastLocation';
import { CheckoutInfoFieldset } from '~/features/cart/checkout/info/CheckoutInfoFieldset';
import { api } from '~/modules';
import { processGraphqlFormErrors } from '~/utils/errors';
import { useFormState, useValidateWithFeedback } from '~/modules/form';
import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import { usePortalHREF_relative, usePortalRouter } from '~/features/global/portal';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useTRPC } from '~/modules/trpc/client';
import { notFound } from 'next/navigation';
import { useCartCtx } from '~/features/cart/context';
import { CheckoutPageTemplate } from '~/features/cart/checkout/CheckoutPageTemplate';
import { PORTAL_RELATIVE_HREF_CART, PORTAL_RELATIVE_HREF_CHECKOUT } from '~/features/cart/hooks';
export const CheckoutInfoPage: React.FC = () => {
  const $ = _c(64);
  const i18n = useI18n();
  const router = usePortalRouter();
  const {
    dispatchGraphqlError
  } = useNotifications();
  const trpc = useTRPC();
  const cartCtx = useCartCtx();
  let t0;
  if ($[0] !== trpc.cart.get) {
    t0 = trpc.cart.get.queryOptions(undefined, {
      select: _temp
    });
    $[0] = trpc.cart.get;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const cartQuery = useSuspenseQuery(t0);
  const lastLocationQuery = useLastLocationQuery();
  const lastLocationRadius = useLastLocationRadius();
  const t1 = cartQuery.data;
  const t2 = cartQuery.data.userProvidesLocation ?? false;
  const t3 = cartQuery.data.address || lastLocationQuery || "";
  const t4 = (cartQuery.data.durationType || CART_DURATION_HALF_DAY) as CartDuration;
  let t5;
  if ($[2] !== cartQuery.data.participants) {
    t5 = CACHED_PARTICIPANTS_FILTER.getValue() ?? cartQuery.data.participants ?? CACHED_PARTICIPANTS_FILTER.initialValue;
    $[2] = cartQuery.data.participants;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== cartQuery.data.startsAt) {
    t6 = maybeMap(cartQuery.data.startsAt, DateTimeSDK.toLocaleTimeStr);
    $[4] = cartQuery.data.startsAt;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = cartQuery.data.radius || lastLocationRadius || 10;
  const t8 = cartQuery.data.budget || undefined;
  const t9 = cartQuery.data.budgetType || null;
  let t10;
  if ($[6] !== cartQuery.data.startsAt) {
    t10 = cartQuery.data.startsAt?.toString() ?? null;
    $[6] = cartQuery.data.startsAt;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== cartQuery.data.endsAt) {
    t11 = cartQuery.data.endsAt?.toString() ?? null;
    $[8] = cartQuery.data.endsAt;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  const t12 = cartQuery.data.meta.dateTimeIndication || null;
  let t13;
  if ($[10] !== cartQuery.data || $[11] !== t10 || $[12] !== t11 || $[13] !== t12 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t13 = {
      ...t1,
      userProvidesLocation: t2,
      address: t3,
      durationType: t4,
      participants: t5,
      startTime: t6,
      radius: t7,
      budget: t8,
      budgetType: t9,
      startsAt: t10,
      endsAt: t11,
      dateTimeIndication: t12
    };
    $[10] = cartQuery.data;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const initialValues = t13;
  const formState = useFormState(initialValues, CheckoutInfoFieldset.useValidation(i18n));
  const validateWithFeedback = useValidateWithFeedback(formState);
  let t14;
  if ($[23] !== cartQuery.data.items) {
    const items = cartQuery.data.items ?? [];
    const needsCustom = items.filter(_temp2);
    const isMixed = needsCustom.length > 0 && needsCustom.length < items.length;
    t14 = {
      hidden: needsCustom.length === 0,
      mixActivityNames: isMixed ? [...new Set(needsCustom.map(_temp3).filter(_temp4))] : []
    };
    $[23] = cartQuery.data.items;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const locationApplicability = t14;
  let t15;
  let t16;
  if ($[25] !== formState.values.participants) {
    t15 = () => {
      if (typeof formState.values.participants === "number" && formState.values.participants >= 1 && CACHED_PARTICIPANTS_FILTER.getValue() !== formState.values.participants) {
        CACHED_PARTICIPANTS_FILTER.setValue(formState.values.participants);
      }
    };
    t16 = [formState.values.participants];
    $[25] = formState.values.participants;
    $[26] = t15;
    $[27] = t16;
  } else {
    t15 = $[26];
    t16 = $[27];
  }
  useEffect(t15, t16);
  let t17;
  if ($[28] !== cartCtx || $[29] !== dispatchGraphqlError || $[30] !== formState || $[31] !== router) {
    t17 = {
      throwOnError: false,
      onSuccess() {
        cartCtx.invalidateQueries();
        router.push(PORTAL_RELATIVE_HREF_CHECKOUT("extra"));
      },
      onError(e) {
        dispatchGraphqlError(e);
        formState.updateErrors(processGraphqlFormErrors(e));
      }
    };
    $[28] = cartCtx;
    $[29] = dispatchGraphqlError;
    $[30] = formState;
    $[31] = router;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const updateCheckoutInfo = api.useGraphqlMutation(UPDATE_CHECKOUT_INFO_MUTATION, t17);
  const t18 = usePortalHREF_relative(PORTAL_RELATIVE_HREF_CART);
  let t19;
  if ($[33] !== i18n) {
    t19 = i18n._("checkout.cta.back-to-cart", undefined, {
      message: "Back to cart"
    });
    $[33] = i18n;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t18 || $[36] !== t19) {
    t20 = {
      href: t18,
      label: t19
    };
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== i18n) {
    t21 = i18n._("checkout.cta.to-extras", undefined, {
      message: "Choose extras"
    });
    $[38] = i18n;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== cartQuery.data.id || $[41] !== formState.values.address || $[42] !== formState.values.budget || $[43] !== formState.values.budgetType || $[44] !== formState.values.dateTimeIndication || $[45] !== formState.values.durationType || $[46] !== formState.values.endsAt || $[47] !== formState.values.participants || $[48] !== formState.values.radius || $[49] !== formState.values.startTime || $[50] !== formState.values.startsAt || $[51] !== formState.values.userProvidesLocation || $[52] !== t21 || $[53] !== updateCheckoutInfo || $[54] !== validateWithFeedback) {
    t22 = {
      label: t21,
      async onClick() {
        if (validateWithFeedback() === false) {
          return;
        }
        await updateCheckoutInfo.mutateAsync({
          cartId: cartQuery.data.id.toString(),
          input: {
            address: formState.values.address,
            budget: formState.values.budget,
            endsAt: formState.values.endsAt ? DateTimeSDK.formatNativeDate(new Date(formState.values.endsAt)) : null,
            startsAt: CheckoutInfoFieldset.parseStartsAt(formState.values.startsAt, formState.values.startTime)?.toISOString() ?? null,
            radius: formState.values.radius,
            budgetType: formState.values.budgetType,
            durationType: formState.values.durationType,
            participants: formState.values.participants ?? 0,
            dateTimeIndication: formState.values.dateTimeIndication,
            userProvidesLocation: formState.values.userProvidesLocation ?? false
          }
        });
      }
    };
    $[40] = cartQuery.data.id;
    $[41] = formState.values.address;
    $[42] = formState.values.budget;
    $[43] = formState.values.budgetType;
    $[44] = formState.values.dateTimeIndication;
    $[45] = formState.values.durationType;
    $[46] = formState.values.endsAt;
    $[47] = formState.values.participants;
    $[48] = formState.values.radius;
    $[49] = formState.values.startTime;
    $[50] = formState.values.startsAt;
    $[51] = formState.values.userProvidesLocation;
    $[52] = t21;
    $[53] = updateCheckoutInfo;
    $[54] = validateWithFeedback;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== formState || $[57] !== i18n || $[58] !== locationApplicability) {
    t23 = <CheckoutInfoFieldset.Component formState={formState} i18n={i18n} locationApplicability={locationApplicability} />;
    $[56] = formState;
    $[57] = i18n;
    $[58] = locationApplicability;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== t20 || $[61] !== t22 || $[62] !== t23) {
    t24 = <CheckoutPageTemplate previousButton={t20} nextButton={t22}>{t23}</CheckoutPageTemplate>;
    $[60] = t20;
    $[61] = t22;
    $[62] = t23;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  return t24;
};
const UPDATE_CHECKOUT_INFO_MUTATION = graphql(`
    mutation updateCheckoutInfo($cartId: ID!, $input: UpdateCheckoutInfoInput!) {
        updateCheckoutInfo(cartId: $cartId, input: $input) {
            cart {
                ...DefaultCart
            }
        }
    }
`);
function _temp(data) {
  return data ? data : notFound();
}
function _temp2(item) {
  if (item.locationType === "ADDRESS") {
    return false;
  }
  if (item.locationType === "CUSTOM") {
    return true;
  }
  return !!item.activity?.hasCustomLocations;
}
function _temp3(item_0) {
  return item_0.activity?.name;
}
function _temp4(name) {
  return !!name;
}